import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { Department } from 'app/models/Department';
import { AccountByCodeResultItem } from 'app/models/general-ledger/AccountsByCodesBatch';

@Injectable({
	providedIn: 'root',
})
export class GeneralLedgerLookupServices {
	private readonly urlPath = `${environment.apiUrl}general-ledger/clients/`;

	// Per (clientId, code) request cache for getAccountByCode. shareReplay(1) collapses identical
	// requests; a 404 still propagates and evicts the key so a later call retries. Entries expire
	// after a short TTL so account edits show up without a reload and the map cannot grow unbounded.
	private static readonly CACHE_TTL_MS = 5 * 60 * 1000;
	private readonly accountByCodeCache = new Map<string, { value: Observable<any>; expiresAt: number }>();

	constructor(private httpClient: HttpClient) {}

	private getOrCreateCached<T>(
		cache: Map<string, { value: Observable<T>; expiresAt: number }>,
		key: string,
		factory: () => Observable<T>
	): Observable<T> {
		const cached = cache.get(key);
		if (cached && cached.expiresAt > Date.now()) {
			return cached.value;
		}
		const value = factory().pipe(
			catchError((error) => {
				cache.delete(key); // allow retry; don't memoize a failure (e.g. a 404)
				return throwError(() => error);
			}),
			shareReplay({ bufferSize: 1, refCount: false })
		);
		cache.set(key, { value, expiresAt: Date.now() + GeneralLedgerLookupServices.CACHE_TTL_MS });
		return value;
	}

	/** Drop the cached single-account lookups (e.g. on client switch or after account edits). */
	clearAccountLookupCache(): void {
		this.accountByCodeCache.clear();
	}

	getGLAccounts(clientId: string, filter: any): Observable<any[]> {
		return this.httpClient.post<any[]>(`${this.urlPath}${clientId}/lookup/search`, filter).pipe();
	}

	getReferenceData(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/lookup/referenceData`).pipe();
	}

	getSubCodesFor(clientId: string, parentId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${this.urlPath}${clientId}/lookup/referenceData/objectCode/${parentId}/subcodes`)
			.pipe();
	}

	getDepartmentsFor(clientId: string, fundId: string): Observable<Department[]> {
		return this.httpClient.get<Department[]>(`${this.urlPath}${clientId}/lookup/funds/${fundId}/departments`).pipe();
	}

	getAccountByCode(clientId: string, code: string): Observable<any> {
		const key = `${clientId}|${code}`;
		return this.getOrCreateCached(this.accountByCodeCache, key, () =>
			this.httpClient.get<any>(`${this.urlPath}${clientId}/lookup/account/${code}`).pipe()
		);
	}

	// Batch of getAccountByCode: resolves many account codes in one request so a form doesn't fire one
	// GET per row. Each result carries the same rich account object the single-item call returns, keyed by
	// the requested code; codes that don't resolve are omitted. Callers gather the distinct codes, call
	// this once, and read each row's account from the returned array by code.
	getAccountsByCodes(clientId: string, codes: string[]): Observable<AccountByCodeResultItem[]> {
		if (!codes || codes.length === 0) {
			return of([]);
		}
		return this.httpClient
			.post<AccountByCodeResultItem[]>(`${this.urlPath}${clientId}/lookup/accounts-by-codes`, codes)
			.pipe(tap((results) => this.seedAccountLookupCache(clientId, results)));
	}

	// A batch-resolved account is the same rich object the single lookup returns, so pre-warm the
	// single-lookup cache with each result. A later getAccountByCode for the same code is then served
	// from cache instead of firing its own GET (e.g. the save-time budget tolerance pass re-resolving
	// the codes an import just fetched). Unresolved codes are absent from the batch and stay unseeded,
	// so the single lookup's 404 behaviour for them is unchanged.
	private seedAccountLookupCache(clientId: string, results: AccountByCodeResultItem[]): void {
		const expiresAt = Date.now() + GeneralLedgerLookupServices.CACHE_TTL_MS;
		for (const item of results ?? []) {
			if (item?.code && item.account != null) {
				this.accountByCodeCache.set(`${clientId}|${item.code}`, { value: of(item.account), expiresAt });
			}
		}
	}

	getAccountsBySubObjectCode(clientId: string, code: string): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.urlPath}${clientId}/lookup/subobjectcode/${code}/accounts`).pipe();
	}

	getconfiguration(clientId: string): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.urlPath}${clientId}/lookup/getconfiguration`).pipe();
	}
	getaccountNumbers(clientId: string): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.urlPath}${clientId}/lookup/getaccountNumbers`).pipe();
	}
	getAllAccounts(clientId: string): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.urlPath}${clientId}/lookup/getallaccounts`).pipe();
	}
}
